import { Button } from 'designSystem/components/Button'
import { ScrollArea } from 'designSystem/components/ScrollArea'
import { useSharedColorScale } from 'features/assetVizAdapter'
import { getPhysicalBoundsForCellBounds } from 'features/dataLayers/generation/shortTermCorrosionRate/algorithmShared'
import type { ShortTermRateInspectionCdfGroup } from 'features/dataLayers/generation/shortTermCorrosionRate/inspectionChoices'
import type {
  ShortTermCorrosionFeatureRectangle,
  ShortTermCorrosionPriorSupport,
  ShortTermRateFeatureBounds,
  ShortTermRateFeatureGridMetadata,
} from 'features/dataLayers/generation/shortTermCorrosionRate/types'
import type { ShortTermCorrosionRateGenerationParams } from 'features/dataLayers/generation/shortTermCorrosionRate/useGenerateShortTermCorrosionRateCDFs'
import { ChevronLeft, ChevronRight, Loader2, Play } from 'lucide-react'
import styled, { keyframes } from 'styled-components'
import type {
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoTMinMethod,
} from 'types'

import { createPriorSupportRectangle } from './featureMarkup'
import type { WorkflowDisplayUnits } from './ParameterControls'
import { RemainingLifeCalculationInspector } from './RemainingLifeCalculationInspector'
import {
  type AsyncStatus,
  FeatureStepPanel,
  InputSelectors,
  MatchesStepPanel,
  WizardStep,
} from './WizardPanels'
import {
  type ShortTermCorrosionWorkflowVisualizationReferenceData,
  WorkflowAssetVizPanel,
} from './WorkflowVizPanel'

// Keeps the prior panel in controlled mode without letting it write back
// into the shared color scale; only the target panel's controls may change it.
function ignoreSharedScaleChange() {
  return undefined
}

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

const PanelLayout = styled.div`
  display: grid;
  min-height: 0;
  flex: 1 1 0%;
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 64rem) {
    grid-template-columns: 1fr 27rem;
  }
`

const PanelMain = styled.main`
  min-height: 0;
  border-bottom: 1px solid var(--border);
  background-color: #000;

  @media (min-width: 64rem) {
    border-bottom: 0;
    border-right: 1px solid var(--border);
  }
`

const MatchesGrid = styled.div`
  display: grid;
  height: 100%;
  min-height: 28rem;
  grid-template-columns: minmax(0, 1fr);
  gap: 1px;
  background-color: var(--border);

  @media (min-width: 48rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const PanelAside = styled.aside`
  display: flex;
  min-height: 0;
  flex-direction: column;
  background-color: var(--card);
`

const PanelScrollArea = styled(ScrollArea)`
  min-height: 0;
  flex: 1 1 0%;
`

const PanelScrollInner = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.25rem;
`

const ErrorBox = styled.div`
  border: 1px solid color-mix(in oklab, var(--destructive) 30%, transparent);
  border-radius: calc(var(--radius) - 2px);
  background-color: color-mix(in oklab, var(--destructive) 10%, transparent);
  padding: 0.75rem;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--destructive);
`

const PanelFooter = styled.footer`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-top: 1px solid var(--border);
  padding: 1.25rem;
`

const FooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
`

const SpinningLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`

type ShortTermInspectionCdfGroup = ShortTermRateInspectionCdfGroup

export type ShortTermWorkflowPrimaryAction = {
  disabled: boolean
  icon: 'next' | 'play'
  label: string
  onClick: () => void
} | null

export function getShortTermWorkflowPrimaryAction({
  step,
  hasRateCdf,
  hasLastRateResult,
  canFindMatches,
  canCalculateRate,
  onFindMatches,
  onViewRate,
  onCalculateRate,
  onCompleteRate,
}: {
  step: WizardStep
  hasRateCdf: boolean
  hasLastRateResult: boolean
  canFindMatches: boolean
  canCalculateRate: boolean
  onFindMatches: () => void
  onViewRate: () => void
  onCalculateRate: () => void
  onCompleteRate: () => void
}): ShortTermWorkflowPrimaryAction {
  if (step === WizardStep.Features) {
    return {
      disabled: !canFindMatches,
      icon: 'next',
      label: 'Find matches',
      onClick: onFindMatches,
    }
  }

  if (step === WizardStep.Matches) {
    return {
      disabled: !hasLastRateResult && !canCalculateRate,
      icon: hasLastRateResult ? 'next' : 'play',
      label: hasLastRateResult ? 'View rate' : 'Calculate rate',
      onClick: hasLastRateResult ? onViewRate : onCalculateRate,
    }
  }

  if (step === WizardStep.Rate && hasRateCdf) {
    return {
      disabled: !hasLastRateResult,
      icon: 'next',
      label: 'Done',
      onClick: onCompleteRate,
    }
  }

  return null
}

export function getShortTermWorkflowFeatureViewState({
  selectedFeature,
  selectedSupport,
  comparisonGrid,
}: {
  selectedFeature?: ShortTermCorrosionFeatureRectangle
  selectedSupport?: ShortTermCorrosionPriorSupport
  comparisonGrid?: ShortTermRateFeatureGridMetadata
}) {
  const selectedFeatureRectangles = selectedFeature ? [selectedFeature] : []
  const selectedPriorRectangles =
    selectedFeature && selectedSupport && comparisonGrid
      ? [
          createPriorSupportRectangle({
            featureRectangle: selectedFeature,
            support: selectedSupport,
            comparisonGrid,
          }),
        ]
      : []
  const selectedPriorZoomBoundsFeet =
    selectedSupport && comparisonGrid
      ? getPhysicalBoundsForCellBounds(
          selectedSupport.priorCellBounds,
          comparisonGrid
        )
      : undefined

  return {
    selectedFeatureRectangles,
    selectedPriorRectangles,
    selectedPriorZoomBoundsFeet,
  }
}

interface ShortTermCorrosionWorkflowPanelViewProps {
  referenceData: ShortTermCorrosionWorkflowVisualizationReferenceData
  assetModel?: GeckoAssetModel
  componentId?: string
  step: WizardStep
  error: string | null
  asyncStatus: AsyncStatus
  targetGroup?: ShortTermInspectionCdfGroup
  comparisonGroup?: ShortTermInspectionCdfGroup
  targetGroups: readonly ShortTermInspectionCdfGroup[]
  comparisonGroups: readonly ShortTermInspectionCdfGroup[]
  targetCdf?: GeckoComponentDataFrame
  comparisonCdf?: GeckoComponentDataFrame
  targetDifferenceFromNominalCdf?: GeckoComponentDataFrame
  targetDifferenceFromNominalCdfs: readonly GeckoComponentDataFrame[]
  rateDisplayCdf?: GeckoComponentDataFrame
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  selectedFeatureId?: string
  selectedFeatureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  selectedPriorRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  selectedFeatureZoomBoundsFeet?: ShortTermRateFeatureBounds
  selectedPriorZoomBoundsFeet?: ShortTermRateFeatureBounds
  priorSupports: readonly ShortTermCorrosionPriorSupport[]
  algorithmPriorSupports: readonly ShortTermCorrosionPriorSupport[]
  observationCount: number
  hasDetected: boolean
  canDetect: boolean
  params: ShortTermCorrosionRateGenerationParams
  displayUnits: WorkflowDisplayUnits
  tminMethod?: GeckoTMinMethod
  hoveredCoords?: GeckoComponentCoords
  forecastComponentConfig?: ForecastComponentConfig
  primaryAction: ShortTermWorkflowPrimaryAction
  onTargetChange: (value: string) => void
  onComparisonChange: (value: string) => void
  onSelectTargetResolution: (cdf: GeckoComponentDataFrame) => void
  onDetect: () => void
  onSelectFeature: (featureId: string) => void
  onDeleteFeature: (featureId: string) => void
  onParamChange: (
    updates: Partial<ShortTermCorrosionRateGenerationParams>
  ) => void
  onSupportShiftChange: (props: {
    featureId: string
    shiftXFeet: number
    shiftYFeet: number
  }) => void
  onSupportShiftReset: (featureId: string) => void
  onBack: () => void
  onHoveredCoordsChange: Parameters<
    typeof WorkflowAssetVizPanel
  >[0]['onHoveredCoordsChange']
}

export function ShortTermCorrosionWorkflowPanelView({
  referenceData,
  assetModel,
  componentId,
  step,
  error,
  asyncStatus,
  targetGroup,
  comparisonGroup,
  targetGroups,
  comparisonGroups,
  targetCdf,
  comparisonCdf,
  targetDifferenceFromNominalCdf,
  targetDifferenceFromNominalCdfs,
  rateDisplayCdf,
  featureRectangles,
  selectedFeatureId,
  selectedFeatureRectangles,
  selectedPriorRectangles,
  selectedFeatureZoomBoundsFeet,
  selectedPriorZoomBoundsFeet,
  priorSupports,
  algorithmPriorSupports,
  observationCount,
  hasDetected,
  canDetect,
  params,
  displayUnits,
  tminMethod,
  hoveredCoords,
  forecastComponentConfig,
  primaryAction,
  onTargetChange,
  onComparisonChange,
  onSelectTargetResolution,
  onDetect,
  onSelectFeature,
  onDeleteFeature,
  onParamChange,
  onSupportShiftChange,
  onSupportShiftReset,
  onBack,
  onHoveredCoordsChange,
}: ShortTermCorrosionWorkflowPanelViewProps) {
  const sharedVizProps = {
    componentId,
    onHoveredCoordsChange,
    referenceData,
  }

  return (
    <PanelLayout>
      <PanelMain>
        {step === WizardStep.Features && (
          <WorkflowAssetVizPanel
            {...sharedVizProps}
            title='Target Difference from Nominal'
            inspection={targetGroup?.inspection}
            cdf={targetDifferenceFromNominalCdf}
            resolutionCdfs={targetDifferenceFromNominalCdfs}
            onSelectResolution={onSelectTargetResolution}
            featureRectangles={featureRectangles}
            selectedFeatureId={selectedFeatureId}
          />
        )}
        {step === WizardStep.Matches && (
          <MatchesCompareGrid
            sharedVizProps={sharedVizProps}
            targetGroup={targetGroup}
            comparisonGroup={comparisonGroup}
            targetCdf={targetCdf}
            comparisonCdf={comparisonCdf}
            onSelectTargetResolution={onSelectTargetResolution}
            selectedFeatureRectangles={selectedFeatureRectangles}
            selectedPriorRectangles={selectedPriorRectangles}
            selectedFeatureId={selectedFeatureId}
            selectedFeatureZoomBoundsFeet={selectedFeatureZoomBoundsFeet}
            selectedPriorZoomBoundsFeet={selectedPriorZoomBoundsFeet}
          />
        )}
        {step === WizardStep.Rate && (
          <WorkflowAssetVizPanel
            {...sharedVizProps}
            title='Short-Term Rate'
            inspection={targetGroup?.inspection}
            cdf={rateDisplayCdf}
            featureRectangles={featureRectangles}
            selectedFeatureId={selectedFeatureId}
          />
        )}
      </PanelMain>

      <PanelAside>
        <PanelScrollArea>
          <PanelScrollInner>
            {error ? <ErrorBox>{error}</ErrorBox> : null}

            <InputSelectors
              targetGroupId={targetGroup?.inspectionId}
              comparisonGroupId={comparisonGroup?.inspectionId}
              targetGroups={targetGroups}
              comparisonGroups={comparisonGroups}
              onTargetChange={onTargetChange}
              onComparisonChange={onComparisonChange}
            />

            {step === WizardStep.Features && (
              <FeatureStepPanel
                featureRectangles={featureRectangles}
                selectedFeatureId={selectedFeatureId}
                hasDetected={hasDetected}
                observationCount={observationCount}
                canDetect={canDetect}
                asyncStatus={asyncStatus}
                params={params}
                displayUnits={displayUnits}
                onDetect={onDetect}
                onSelectFeature={onSelectFeature}
                onDeleteFeature={onDeleteFeature}
                onParamChange={onParamChange}
              />
            )}

            {step === WizardStep.Matches && (
              <MatchesStepPanel
                featureRectangles={featureRectangles}
                priorSupports={priorSupports}
                algorithmPriorSupports={algorithmPriorSupports}
                selectedFeatureId={selectedFeatureId}
                displayUnits={displayUnits}
                onSelectFeature={onSelectFeature}
                onSupportShiftChange={onSupportShiftChange}
                onSupportShiftReset={onSupportShiftReset}
              />
            )}

            {step === WizardStep.Rate && (
              <RemainingLifeCalculationInspector
                assetModel={assetModel}
                componentId={componentId}
                inspection={targetGroup?.inspection}
                comparisonInspection={comparisonGroup?.inspection}
                thicknessCdf={targetCdf}
                shortTermRateCdf={rateDisplayCdf}
                hoveredCoords={hoveredCoords}
                forecastComponentConfig={forecastComponentConfig}
                tminMethod={tminMethod}
              />
            )}
          </PanelScrollInner>
        </PanelScrollArea>

        <PanelFooter>
          <Button
            variant='outline'
            disabled={asyncStatus === 'running'}
            onClick={onBack}>
            <ChevronLeft size={14} />
            Back
          </Button>

          <FooterActions>
            {primaryAction ? (
              <Button
                disabled={asyncStatus === 'running' || primaryAction.disabled}
                onClick={primaryAction.onClick}>
                <WorkflowActionIcon
                  kind={primaryAction.icon}
                  running={asyncStatus === 'running'}
                />
                {primaryAction.label}
              </Button>
            ) : null}
          </FooterActions>
        </PanelFooter>
      </PanelAside>
    </PanelLayout>
  )
}

function MatchesCompareGrid({
  sharedVizProps,
  targetGroup,
  comparisonGroup,
  targetCdf,
  comparisonCdf,
  onSelectTargetResolution,
  selectedFeatureRectangles,
  selectedPriorRectangles,
  selectedFeatureId,
  selectedFeatureZoomBoundsFeet,
  selectedPriorZoomBoundsFeet,
}: {
  sharedVizProps: Pick<
    Parameters<typeof WorkflowAssetVizPanel>[0],
    'componentId' | 'onHoveredCoordsChange' | 'referenceData'
  >
  targetGroup?: ShortTermInspectionCdfGroup
  comparisonGroup?: ShortTermInspectionCdfGroup
  targetCdf?: GeckoComponentDataFrame
  comparisonCdf?: GeckoComponentDataFrame
  onSelectTargetResolution: (cdf: GeckoComponentDataFrame) => void
  selectedFeatureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  selectedPriorRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  selectedFeatureId?: string
  selectedFeatureZoomBoundsFeet?: ShortTermRateFeatureBounds
  selectedPriorZoomBoundsFeet?: ShortTermRateFeatureBounds
}) {
  const sharedColorScale = useSharedColorScale(targetCdf)

  return (
    <MatchesGrid>
      <WorkflowAssetVizPanel
        {...sharedVizProps}
        title='Target'
        inspection={targetGroup?.inspection}
        cdf={targetCdf}
        resolutionCdfs={targetGroup?.cdfs}
        onSelectResolution={onSelectTargetResolution}
        featureRectangles={selectedFeatureRectangles}
        selectedFeatureId={selectedFeatureId}
        zoomBoundsFeet={selectedFeatureZoomBoundsFeet}
        selectedColorThreshold={sharedColorScale.selectedColorThreshold}
        onSelectedColorThresholdChange={
          sharedColorScale.onSelectedColorThresholdChange
        }
        filteredValueRange={sharedColorScale.filteredValueRange}
        onFilteredValueRangeChange={sharedColorScale.onFilteredValueRangeChange}
      />
      <WorkflowAssetVizPanel
        {...sharedVizProps}
        title='Prior'
        inspection={comparisonGroup?.inspection}
        cdf={comparisonCdf}
        featureRectangles={selectedPriorRectangles}
        selectedFeatureId={selectedFeatureId}
        zoomBoundsFeet={selectedPriorZoomBoundsFeet}
        selectedColorThreshold={sharedColorScale.selectedColorThreshold}
        onSelectedColorThresholdChange={ignoreSharedScaleChange}
        filteredValueRange={sharedColorScale.filteredValueRange}
        onFilteredValueRangeChange={ignoreSharedScaleChange}
        showColorScaleSettings={false}
      />
    </MatchesGrid>
  )
}

function WorkflowActionIcon({
  kind,
  running,
}: {
  kind: 'next' | 'play'
  running: boolean
}) {
  if (running) return <SpinningLoader size={14} />

  const Icon = kind === 'play' ? Play : ChevronRight
  return <Icon size={14} />
}
